import { useRef } from "react";
import { Clickable } from "../../../../components/Clickable";
import styled from "styled-components";
import { Rounded } from "../../../../theme/Rounded";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { TestIDs } from "../../../../tests/types/testIds";
import {
  chatNeedsAttention,
  chatLastMessageIcon,
  chatLastMessagePreview,
  formatChatListTimestamp,
  isChatHandledByAgent,
} from "../../utils/integration-utils";
import { faHandPaper } from "@fortawesome/free-solid-svg-icons/faHandPaper";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons/faCircleUser";
import { TextWithTruncated } from "../../../../components/TextWithTruncated";
import { whatsappChatScrollToBottom } from "../../utils/integration-utils";
import { useTranslation } from "react-i18next";
import { Tooltip } from "../../../../utils/local-antd";
import { Spacing } from "../../../../theme/Spacing";
import { Conversation } from "../../../../model/integration/dto/meta/MetaConversation";
import { whatsappChatTitle } from "../../utils/whatsapp-chat-title";
import { WAMetaMessageDirection } from "../../../../model/integration/types/whatsapp/WAMetaMessageDirection.enum";
import { WAMessageTicks } from "./components/WAMessageTicks";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppSelector } from "../../../../redux/store";
import { TagList } from "../../../../components/TagList";

/** Clickable does not stretch on its own, which let the row's content run past
 * the panel edge and clip the timestamp.
 */
const RowButton = styled(Clickable)`
  display: block;
  width: 100%;
  min-width: 0;
`;

const ChatRow = styled(Flex)<{ $active: boolean; $needsAttention: boolean }>`
  width: 100%;
  min-width: 0;
  padding: ${Spacing.s} ${Spacing.m};
  cursor: pointer;
  align-items: center;
  gap: ${Spacing.s};
  min-height: 4.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.sub};
  /* The accent doubles as the queue marker. Active still wins - you can see
     which chat is open from the panel beside it, but the flag is the only
     thing telling you the agent walked away from the others. */
  border-inline-start: 3px solid
    ${({ $active, $needsAttention, theme }) =>
      $active
        ? theme.main
        : $needsAttention
          ? theme.warning
          : theme.transparent};
  background: ${({ $active, theme }) => ($active ? theme.sub : theme.white)};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.sub : theme.lightSub};
  }
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: ${Rounded.circle};
  background: ${({ theme }) => theme.lightSub};
  color: ${({ theme }) => theme.main};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const UnreadBadge = styled(Flex)`
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 ${Spacing.xs};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.white};
`;

/** The counterpart to the robot icon. While the agent owns a chat the row says
 * so; when it walks away that icon simply vanished, and an absence is not
 * something anyone notices while scrolling a list.
 */
const AttentionPill = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  flex-shrink: 0;
  padding: 0 ${Spacing.xs};
  height: 1.15rem;
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.warning};
`;

const Preview = styled(Text)<{ $unread: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  unicode-bidi: plaintext;
  text-align: start;
  font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
`;

type Props = {
  chat: Conversation;
  activeChatId: string;
  setActiveChatId: (chatId: string) => void;
};

export const WAContactRow: React.FC<Props> = ({
  chat,
  activeChatId,
  setActiveChatId,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const handledByAgent = isChatHandledByAgent(chat, integration);
  const needsAttention = chatNeedsAttention(chat);

  const getPlaceholderMessage = () => chatLastMessagePreview(chat, t);

  const getUnreadCount = () => {
    return chat.unreadCount || 0;
  };

  const onChatClick = async (chatId: string) => {
    setActiveChatId(chatId);
  };

  const unread = getUnreadCount();

  return (
    <RowButton
      ref={ref}
      onClick={() => onChatClick(chat._id)}
      key={chat._id}
      dataTestId={TestIDs.integration.whatsappMeta.chat.row(
        chat.waId || chat._id,
      )}
    >
      <ChatRow
        $active={chat._id === activeChatId}
        $needsAttention={needsAttention}
      >
        <Avatar>
          <AtiraIcon icon={faCircleUser} size="2x" color="main" />
        </Avatar>

        <Flex flexDirection="column" flex={1} gap="xs" minWidth="0">
          <Flex alignItems="center" gap="xs" minWidth="0">
            <TextWithTruncated
              toolTip={false}
              fontSize="m"
              color="textColor"
              fontWeight={unread ? "bold" : undefined}
              length={20}
            >
              {whatsappChatTitle(chat)}
            </TextWithTruncated>

            {handledByAgent ? (
              <Tooltip
                title={t("integration.whatsapp.meta.aiAgent.banner.title", {
                  name:
                    integration?.config?.aiAgent?.name ||
                    t("integration.whatsapp.meta.aiAgent.defaultName"),
                })}
              >
                <span />
                <AtiraIcon icon={faRobot} color="main" size="xs" />
              </Tooltip>
            ) : null}

            {needsAttention ? (
              <Tooltip
                title={
                  chat.handover?.reason ||
                  t("integration.whatsapp.meta.handover.needsReply")
                }
              >
                <span />
                <AttentionPill>
                  <AtiraIcon icon={faHandPaper} color="white" size="xs" />
                  <Text fontSize="xs" color="white" style={{ lineHeight: 1 }}>
                    {t("integration.whatsapp.meta.handover.needsReply")}
                  </Text>
                </AttentionPill>
              </Tooltip>
            ) : null}

            {chat.lastMessageAt ? (
              <Flex marginInlineStart="auto">
                <Text
                  fontSize="xs"
                  color={unread ? "main" : "darkerSub"}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {formatChatListTimestamp(chat.lastMessageAt)}
                </Text>
              </Flex>
            ) : null}
          </Flex>

          <Flex alignItems="center" gap="s" minWidth="0">
            <Flex alignItems="center" gap="xs" minWidth="0" flexShrink={1}>
              {chat.lastDirection === WAMetaMessageDirection.OUTGOING ? (
                <Flex flexShrink={0} alignItems="center">
                  <WAMessageTicks status={chat.lastMessageStatus} />
                </Flex>
              ) : null}

              {chat.lastMessageType && chat.lastMessageType !== "text" ? (
                <Flex flexShrink={0} alignItems="center">
                  <AtiraIcon
                    icon={chatLastMessageIcon(chat.lastMessageType)}
                    color="darkerSub"
                    size="sm"
                  />
                </Flex>
              ) : null}

              <Preview color="darkerSub" fontSize="s" $unread={Boolean(unread)}>
                {getPlaceholderMessage()}
              </Preview>
            </Flex>

            {unread ? (
              <UnreadBadge marginInlineStart="auto">
                <Text fontSize="xs" color="white">
                  {unread > 99 ? "99+" : unread}
                </Text>
              </UnreadBadge>
            ) : null}
          </Flex>

          {chat.tagIds?.length ? (
            <Flex minWidth="0" overflow="hidden">
              <TagList tagIds={chat.tagIds} max={2} />
            </Flex>
          ) : null}
        </Flex>
      </ChatRow>
    </RowButton>
  );
};
